import { useState } from 'react';
import { Alert, Button } from 'antd';
import { CheckOutlined } from '@ant-design/icons';
import { acknowledgeTask } from '@/api/dataroom';
import type { Task, TaskStatus } from '@/api/types';
import { useI18n } from '@/i18n/useI18n';
import { useTaskText } from './taskText';

/**
 * ACKNOWLEDGE task: the applicant reads the title + "why we ask" (rendered by the card) and confirms.
 * A single large, full-width button — a clear, unmissable touch target.
 */
export function AcknowledgeTask({
  task,
  onStatus,
}: {
  task: Task;
  onStatus: (status: TaskStatus) => void;
}) {
  const { t } = useI18n();
  const text = useTaskText(task);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (task.status === 'ACCEPTED' || done) {
    return <Alert type="success" showIcon message={t('acknowledge.confirmed')} />;
  }

  async function confirm() {
    setBusy(true);
    setError(null);
    try {
      const res = await acknowledgeTask(task.taskId);
      onStatus(res.status);
      setDone(true);
    } catch {
      setError(t('error.inline'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="dr-stack">
      <div className="dr-meta">{text('statement', 'acknowledge.readPrompt')}</div>
      {error && <Alert type="error" showIcon message={error} />}
      <Button
        type="primary"
        size="large"
        block
        loading={busy}
        icon={<CheckOutlined />}
        onClick={confirm}
      >
        {busy ? t('acknowledge.confirming') : text('submit', 'acknowledge.confirm')}
      </Button>
    </div>
  );
}
